import React, { useEffect, useState } from "react";  
import { Checkbox, IconButton, Stack, TextField, Typography } from "@mui/material";
import { StyledAutocompleteInput } from "../../../shared/Components/StyledAutocompleteInput";
import { useTBContext } from "../../Contexts/useTBStates";
import DeleteIcon from '@mui/icons-material/Delete';


interface ChallengeOptionsProps {
    challengeIndex: number;
}

/** If `hole` is taken by another challenge row, step in `direction` (+1 / -1) until free or bounds; then scan 1..maxHole. */
function resolveHoleAgainstOthers(
    list: any[],
    myIndex: number,
    prevHole: number,
    requested: number,
    maxHole: number,
): number {
    if (!Number.isFinite(requested)) return prevHole;
    if (requested <= 0) return requested;

    const takenByOther = (h: number) =>
        h > 0 && list.some((c: any, i: number) => i !== myIndex && Number(c.hole) === h);

    if (!takenByOther(requested)) return requested;

    const direction = requested >= prevHole ? 1 : -1;
    let h = requested;
    for (let k = 0; k < Math.max(maxHole, 18) * 2; k++) {
        h += direction;
        if (h < 1 || h > maxHole) break;
        if (!takenByOther(h)) return h;
    }
    for (let j = 1; j <= maxHole; j++) {
        if (!takenByOther(j)) return j;
    }
    return prevHole;
}

export const ChallengeOptions = ({ challengeIndex }: ChallengeOptionsProps) => {

    const { appTheme, factor, editMode, viewEvent, roundValue, optionsLoaded, setViewEvent } = useTBContext();

    const [challenge, setChallenge] = useState<any>(null);
    const [challengeOption, setChallengeOption] = useState<any>([]);

    useEffect(() => {
        const round = viewEvent?.rounds?.[roundValue - 1];
        const challengeData = round?.challenges?.[challengeIndex];
        if (!round?.challenges) return;
        const challengeName = challengeData?.type;
        setChallenge(challengeData ?? null);
        if (challengeName !== '') {
            const challengesOpt = optionsLoaded?.find((item: any) => item.keyName === 'Challenges') as { options?: Array<{ type: string }> } | undefined;
            if (challengesOpt?.options) {
                setChallengeOption(challengesOpt.options.filter((item: any) => item.type === challengeName));
            }
        }
    }, [viewEvent, roundValue, challengeIndex]);

    const handleChallengeChange = (key: any, value: any) => {
        if (!viewEvent) return;
        const round = viewEvent.rounds?.[roundValue - 1];
        if (!round?.challenges?.[challengeIndex]) return;
        round.challenges[challengeIndex][key] = value;
        setViewEvent({...viewEvent});
    };

    return (
        <Stack direction={'row'} spacing={1} alignItems={'center'}>

            {challenge?.type && challenge?.type !== '' && (

                Object.keys(challenge).filter((key: any) => !['type', 'name'].includes(key)).map((key: any) => {
                    return (
                        
                        <>
                            {typeof challenge[key] === 'boolean' && (
                                <Stack direction={'column'} spacing={0} width={100*factor} padding={'0px 0px'}
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Typography variant='subtitle2' sx={{ fontWeight: 500, textAlign: 'center' }}>
                                        {key.split(/(?=[A-Z])/).map((word: any) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                    </Typography>
                                    <Checkbox disabled={!editMode} checked={challenge[key]} onChange={(event) => {
                                        handleChallengeChange(key, event.target.checked);
                                    }}/>
                                </Stack>
                            )}
                            
                            {key === 'hole' && (
                                <Stack direction={'column'} spacing={0} width={100*factor} padding={'0px 0px'}
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <TextField disabled={!editMode} value={challenge[key]} variant='outlined'
                                        label={'Hole'}
                                        type='number'
                                        onChange={(event) => {
                                            const raw = Number(event.target.value);
                                            const n = Number.isFinite(raw) ? raw : 0;
                                            if (!viewEvent) return;
                                            const round = viewEvent.rounds?.[roundValue - 1];
                                            const list = round?.challenges ?? [];
                                            const maxHole = Math.max(1, Number(viewEvent.totalHoles) || 18);
                                            const prevHole = Number(challenge.hole);
                                            const resolved = resolveHoleAgainstOthers(
                                                list,
                                                challengeIndex,
                                                prevHole,
                                                n,
                                                maxHole,
                                            );
                                            handleChallengeChange(key, resolved);
                                        }}
                                        sx={{
                                            padding: 0,
                                            "& .MuiInputBase-input": { fontSize: 12 * (factor ? factor : 1), height: 12 * (factor ? factor : 1), padding: 1 },
                                            '& .MuiOutlinedInput-root': { 
                                                padding: '5px 10px',
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: appTheme.palette.primary.main, // Keep the border green when focused
                                                },  
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: appTheme.palette.primary.main, // Always green outline
                                                },
                                            },
                                            '& input': { textAlign: 'left', lineHeight: 'normal' },
                                        }}
                                        inputProps={{
                                            inputMode: "text",
                                            autoComplete: "off",
                                            sx: {
                                                fontSize: 12 * (factor ? factor : 1),
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: appTheme.palette.primary.main,
                                                },
                                            },
                                        }}
                                        InputLabelProps={{
                                            sx: { fontSize: 14 * (factor ? factor : 1), lineHeight: 'normal', padding: '0px 10px', backgroundColor: 'white', marginTop: '0px' },
                                            shrink: true
                                        }}
                                    />
                                </Stack>
                            )}
                        </>
                    )
                })
            )}

            <IconButton 
                disabled={!editMode}
                sx={{
                    color: !editMode ? 'gray' : 'primary.main'
                }}
                onClick={() => {
                    if (!viewEvent) return;
                    const round = viewEvent.rounds?.[roundValue - 1];
                    if (!round?.challenges) return;
                    const newChallenges = JSON.parse(JSON.stringify(round.challenges));
                    newChallenges.splice(challengeIndex, 1);
                    const rounds = viewEvent.rounds ?? [];
                    setViewEvent({...viewEvent, rounds: [...rounds.slice(0, roundValue - 1), {...round, challenges: newChallenges}, ...rounds.slice(roundValue)]});
                }}>
                <DeleteIcon sx={{ color: !editMode ? 'gray' : appTheme.palette.error.main }} />
            </IconButton>

        </Stack>
    )
}